import consola from 'consola'

/**
 * Subscription to a newsletter from a Poool Engage popin (PS-226).
 *
 * This helper loads the Poool Engage SDK, creates the popins and listens to
 * the `formSubmit` event of the SDK. On a submit, it sends the email to the
 * WordPress endpoint, which creates the profile in Batch.
 *
 * `loadSdk()` is the only place that loads the SDK. On the public sites,
 * Didomi is on the page and the SDK waits for the consent of the reader to
 * the Poool vendor. Without Didomi (development), the SDK loads at once.
 *
 * When another script (for example a GTM tag) loads the SDK first, the helper
 * does not load it again: it waits for `window.Engage` and binds to it.
 *
 * Poool waits for the value of the listener before it shows the success
 * state. `null` means success. A `PooolFormError` shows a message under the
 * form and blocks the success state (PS-224).
 */

interface PooolFormSubmitEvent {
  fields?: Record<string, unknown>
  valid?: Record<string, boolean>
  element?: { name?: string, slug?: string }
}

/** The error that Poool shows under the form. `message` is a text key. */
interface PooolFormError {
  fieldKey: 'form'
  message: string
}

/** The SDK. `Engage.init()` returns the same global instance. */
interface PooolEngage {
  init: (appId: string) => PooolEngage
  config: (name: string, value: unknown) => PooolEngage
  autoCreate: () => PooolEngage
  on: (name: string, callback: (event: PooolFormSubmitEvent) => unknown) => PooolEngage
  texts: (texts: Record<string, string>, readOnly?: boolean, locale?: string) => PooolEngage
}

/** The config that `PooolProvider` injects on each public page. */
interface PooolNewsletterConfig {
  url: string
  nonce: string
  /** The Poool application id. Empty when the theme must not load the SDK. */
  appId?: string
}

interface Subscription {
  email: string
  slug: string
}

/** The part of the Didomi SDK that this helper uses. */
interface DidomiSdk {
  getCurrentUserStatus: () => { vendors: Record<string, { enabled?: boolean } | undefined> }
  on: (event: string, callback: () => void) => void
}

declare global {
  interface Window {
    Engage?: PooolEngage
    Didomi?: DidomiSdk
    /** Queue that `index.php` defines when Didomi is on the page. */
    runAfterDidomiReady?: (callback: () => void) => void
    hemicyclePooolNewsletter?: PooolNewsletterConfig
    dataLayer: any[]
  }
}

/** Texts of the popin when the subscription fails. Keep the keys in camelCase. */
const ERROR_TEXTS: Record<string, string> = {
  pooolNlError: "L'inscription n'a pas pu aboutir. Merci de réessayer plus tard.",
  pooolNlRateLimited: 'Trop de tentatives. Merci de réessayer dans quelques minutes.'
}

/**
 * Each Poool popin has its own key for the email field.
 * A known key gives the newsletter slug directly.
 */
const EMAIL_FIELDS: Record<string, string> = {
  field755244: 'l_instant_replays',
  field805987: 'alerte_info',
  userEmail: 'la_quotidienne'
}

/**
 * Fallback for a popin with an unknown field key: the name of the popin in
 * Poool gives the newsletter.
 */
const ELEMENT_NAMES: Array<{ pattern: RegExp, slug: string }> = [
  { pattern: /replay/i, slug: 'l_instant_replays' },
  { pattern: /alerte/i, slug: 'alerte_info' },
  { pattern: /quotidienne/i, slug: 'la_quotidienne' }
]

/** The key of the consent box for the open tracking pixel (PS-198). */
const CONSENT_PIXEL_KEY = /consent.*pixel|pixel.*consent/i

/**
 * The text values that the endpoint reads as a consent.
 * Keep this list equal to `ConsentFlagHelper::TRUTHY` on the PHP side.
 */
const CONSENT_TRUTHY = ['1', 'true', 'on', 'yes']

const SDK_URL = 'https://assets.poool.fr/engage.min.js'

/** The options of the SDK. Keep them equal to the former GTM tag. */
const SDK_CONFIG: Record<string, boolean> = {
  gtagAutoTrackingEnabled: true,
  pianoAutoTrackingEnabled: true
}

/** The id of the Poool vendor in Didomi. Preprod and prod share the notice. */
const DIDOMI_VENDOR_ID = 'poool-VyhCit7N'

const SDK_POLL_INTERVAL = 250
const SDK_POLL_TIMEOUT = 30000

let bound = false
let sdkLoaded = false

/**
 * Find the email and the newsletter slug of the submitted form.
 * Return null when the form is not a known newsletter form.
 */
export const resolveSubscription = (fields: Record<string, unknown>, elementName: string): Subscription | null => {
  for (const key of Object.keys(EMAIL_FIELDS)) {
    const value = fields[key]
    if (typeof value === 'string' && value !== '') {
      return { email: value, slug: EMAIL_FIELDS[key] }
    }
  }

  // Unknown key: take the first field that holds an email address.
  const email = Object.keys(fields)
    .map((key) => fields[key])
    .find((value): value is string => typeof value === 'string' && value.indexOf('@') > 0)

  if (email === undefined) {
    return null
  }

  const match = ELEMENT_NAMES.find(({ pattern }) => pattern.test(elementName))
  if (match === undefined) {
    return null
  }

  consola.warn('[poool-nl] clé email inconnue, newsletter déduite du nom de la popin :', elementName)

  return { email, slug: match.slug }
}

/**
 * Return true only when the reader checked the consent box of the pixel.
 * Return undefined when the box is absent or unchecked: the endpoint then
 * sets no consent. Never read another box, such as the newsletter opt-in.
 */
export const resolveConsentPixel = (fields: Record<string, unknown>): true | undefined => {
  const key = Object.keys(fields).find((key) => CONSENT_PIXEL_KEY.test(key))

  if (key === undefined) {
    return undefined
  }

  const value = fields[key]

  if (value === true || value === 1) {
    return true
  }

  return typeof value === 'string' && CONSENT_TRUTHY.includes(value.trim().toLowerCase()) ? true : undefined
}

/**
 * Send each failure to GTM, so that we can count the lost subscriptions.
 */
const report = (reason: string, elementName: string): void => {
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({
    event: 'poool_nl_error',
    poool_nl_reason: reason,
    poool_nl_element: elementName
  })
}

/**
 * Build the error that Poool shows under the form.
 */
const fail = (reason: string, elementName: string, textKey = 'pooolNlError'): PooolFormError => {
  report(reason, elementName)
  consola.error('[poool-nl] KO', reason, elementName)

  return { fieldKey: 'form', message: textKey }
}

/**
 * Send the subscription to the WordPress endpoint.
 */
const subscribe = async (subscription: Subscription, consentPixel: true | undefined, elementName: string): Promise<PooolFormError | null> => {
  const config = window.hemicyclePooolNewsletter

  if (config?.url === undefined || config.nonce === undefined || config.url === '' || config.nonce === '') {
    consola.warn('[poool-nl] config absente — endpoint indisponible')
    return fail('config_missing', elementName)
  }

  let response: Response

  try {
    response = await fetch(config.url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userEmail: subscription.email,
        slugs: JSON.stringify([subscription.slug]),
        nonce: config.nonce,
        // `undefined` removes the key from the JSON body (PS-198).
        consentPixel
      })
    })
  } catch (error) {
    consola.error('[poool-nl] KO', error)
    return fail('network', elementName)
  }

  if (response.status === 429) {
    consola.warn('[poool-nl] rate-limit atteint, retry-after=', response.headers.get('Retry-After'))
    return fail('rate_limited', elementName, 'pooolNlRateLimited')
  }

  if (!response.ok) {
    return fail(`http_${response.status}`, elementName)
  }

  // The endpoint accepted the subscription: an unreadable body is not an error.
  response.json()
    .then((data) => consola.trace('[poool-nl] OK', data))
    .catch(() => consola.trace('[poool-nl] OK'))

  return null
}

/**
 * Handle the `formSubmit` event of Poool.
 */
const handleFormSubmit = async (event: PooolFormSubmitEvent): Promise<PooolFormError | null> => {
  const fields = event?.fields ?? {}
  const elementName = event?.element?.name ?? ''
  const subscription = resolveSubscription(fields, elementName)

  if (subscription === null) {
    // Unknown form: do not block it, but count it.
    report('no_email_field', elementName)
    consola.warn('[poool-nl] aucun champ email reconnu', elementName)
    return null
  }

  return await subscribe(subscription, resolveConsentPixel(fields), elementName)
}

/**
 * Register the texts and the listener on the SDK. Run once.
 */
const bind = (engage: PooolEngage): void => {
  if (bound) {
    return
  }
  bound = true

  // Poool reads the texts when it creates a popin: register them first.
  engage.texts(ERROR_TEXTS, false, 'fr')
  engage.texts(ERROR_TEXTS, false, 'en')

  engage.on('formSubmit', async (event) => await handleFormSubmit(event))

  consola.trace('[poool-nl] listener bound')
}

/**
 * Load the SDK, then create the popins.
 * Register the texts and the listener before `autoCreate()`: Poool reads
 * them when it creates a popin.
 */
const loadSdk = (appId: string): void => {
  if (sdkLoaded) {
    return
  }
  sdkLoaded = true

  const script = document.createElement('script')
  script.async = true
  script.src = SDK_URL

  script.onload = () => {
    if (window.Engage === undefined) {
      consola.error('[poool-nl] SDK Poool chargé mais Engage absent')
      return
    }

    const engage = window.Engage.init(appId)

    for (const name of Object.keys(SDK_CONFIG)) {
      engage.config(name, SDK_CONFIG[name])
    }

    bind(engage)
    engage.autoCreate()
  }

  script.onerror = () => {
    consola.error('[poool-nl] SDK Poool non chargé', SDK_URL)
  }

  document.head.appendChild(script)
}

/** Return true when another script already loads the SDK. */
const hasExternalSdk = (): boolean => document.querySelector(`script[src^="${SDK_URL}"]`) !== null

/** Return true when the reader gave the consent to the Poool vendor. */
const hasPooolConsent = (didomi: DidomiSdk): boolean => didomi.getCurrentUserStatus().vendors[DIDOMI_VENDOR_ID]?.enabled ?? false

/**
 * Load the SDK when the reader consents to the Poool vendor in Didomi.
 * Without Didomi on the page (development), load the SDK at once.
 */
const loadSdkWithConsent = (appId: string): void => {
  if (typeof window.runAfterDidomiReady !== 'function') {
    loadSdk(appId)
    return
  }

  window.runAfterDidomiReady(() => {
    const didomi = window.Didomi

    if (didomi === undefined) {
      consola.warn('[poool-nl] Didomi prêt mais absent, SDK Poool non chargé')
      return
    }

    if (hasPooolConsent(didomi)) {
      loadSdk(appId)
      return
    }

    consola.trace('[poool-nl] pas de consentement Poool, SDK en attente')

    didomi.on('consent.changed', () => {
      if (hasPooolConsent(didomi)) {
        loadSdk(appId)
      }
    })
  })
}

/**
 * Wait for the SDK that another script loads, then bind.
 * The texts come after `autoCreate()` in that case: Poool shows the keys
 * raw until the popins are created again.
 */
const waitForSdk = (): void => {
  const start = Date.now()
  const timer = window.setInterval(() => {
    if (window.Engage !== undefined) {
      window.clearInterval(timer)
      bind(window.Engage)
      return
    }

    if (Date.now() - start > SDK_POLL_TIMEOUT) {
      window.clearInterval(timer)
      consola.trace('[poool-nl] SDK Poool absent, listener non posé')
    }
  }, SDK_POLL_INTERVAL)
}

/**
 * Bind to an SDK that is already here, or load it, or wait for it.
 */
const init = (): void => {
  if (window.Engage !== undefined) {
    bind(window.Engage)
    return
  }

  const appId = window.hemicyclePooolNewsletter?.appId ?? ''

  if (appId !== '' && !hasExternalSdk()) {
    loadSdkWithConsent(appId)
    return
  }

  waitForSdk()
}

const PooolNewsletterHelper = { init }

export default PooolNewsletterHelper
